/**
 * ChatContext
 * 
 * Global context for chat state management.
 * Handles:
 * - Chat open/closed state
 * - Multiple conversation tabs
 * - Message history per tab
 * - localStorage persistence
 * - 1-hour session auto-expiry
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ChatContext = createContext(null);

const STORAGE_KEY = 'pleasance_chat_state';
const SESSION_EXPIRY_MS = 60 * 60 * 1000; // 1 hour in milliseconds

// Generate unique ID for tabs
const generateTabId = () => `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Default state
const createDefaultState = () => ({
    isOpen: false,
    tabs: [{
        id: generateTabId(),
        title: 'New Chat',
        messages: [],
        createdAt: new Date().toISOString()
    }],
    activeTabId: null
});

// Check if a tab has expired (older than 1 hour)
const isTabExpired = (tab) => {
    const lastActivity = tab.lastActivityAt || tab.createdAt;
    if (!lastActivity) return true;
    const elapsed = Date.now() - new Date(lastActivity).getTime();
    return elapsed > SESSION_EXPIRY_MS;
};

export function ChatProvider({ children }) {
    const [state, setState] = useState(() => {
        // Load from localStorage on init
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                // Filter out expired tabs (1-hour auto-wipe)
                const validTabs = (parsed.tabs || []).filter(tab => !isTabExpired(tab));

                // If all tabs expired, start fresh
                if (validTabs.length === 0) {
                    console.log('All chat sessions expired (1-hour limit). Starting fresh.');
                    return createDefaultState();
                }

                return {
                    ...parsed,
                    tabs: validTabs,
                    activeTabId: validTabs.some(t => t.id === parsed.activeTabId)
                        ? parsed.activeTabId
                        : validTabs[0]?.id,
                    isOpen: false // Always start closed
                };
            }
        } catch (e) {
            console.error('Failed to load chat state:', e);
        }
        return createDefaultState();
    });

    // Set initial active tab
    useEffect(() => {
        if (!state.activeTabId && state.tabs.length > 0) {
            setState(prev => ({ ...prev, activeTabId: prev.tabs[0].id }));
        }
    }, []);

    // Persist to localStorage on change
    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify({
                tabs: state.tabs,
                activeTabId: state.activeTabId
            }));
        } catch (e) {
            console.error('Failed to save chat state:', e);
        }
    }, [state.tabs, state.activeTabId]);

    // Toggle chat open/closed
    const toggleChat = useCallback(() => {
        setState(prev => ({ ...prev, isOpen: !prev.isOpen }));
    }, []);

    // Open chat
    const openChat = useCallback(() => {
        setState(prev => ({ ...prev, isOpen: true }));
    }, []);

    // Close chat
    const closeChat = useCallback(() => {
        setState(prev => ({ ...prev, isOpen: false }));
    }, []);

    // Switch active tab
    const setActiveTab = useCallback((tabId) => {
        setState(prev => ({ ...prev, activeTabId: tabId }));
    }, []);

    // Create new tab
    const createTab = useCallback(() => {
        const newTab = {
            id: generateTabId(),
            title: 'New Chat',
            messages: [],
            createdAt: new Date().toISOString()
        };
        setState(prev => ({
            ...prev,
            tabs: [...prev.tabs, newTab],
            activeTabId: newTab.id
        }));
        return newTab.id;
    }, []);

    // Close tab
    const closeTab = useCallback((tabId) => {
        setState(prev => {
            const newTabs = prev.tabs.filter(t => t.id !== tabId);

            // If closing all tabs, create a new one
            if (newTabs.length === 0) {
                const newTab = {
                    id: generateTabId(),
                    title: 'New Chat',
                    messages: [],
                    createdAt: new Date().toISOString()
                };
                return {
                    ...prev,
                    tabs: [newTab],
                    activeTabId: newTab.id
                };
            }

            // If closing active tab, switch to another
            let newActiveId = prev.activeTabId;
            if (prev.activeTabId === tabId) {
                const closedIndex = prev.tabs.findIndex(t => t.id === tabId);
                newActiveId = newTabs[Math.min(closedIndex, newTabs.length - 1)]?.id;
            }

            return {
                ...prev,
                tabs: newTabs,
                activeTabId: newActiveId
            };
        });
    }, []);

    // Add message to active tab (also updates lastActivityAt for session expiry)
    const addMessage = useCallback((message) => {
        setState(prev => {
            const updatedTabs = prev.tabs.map(tab => {
                if (tab.id === prev.activeTabId) {
                    const newMessages = [...tab.messages, {
                        id: `msg_${Date.now()}`,
                        ...message,
                        timestamp: new Date().toISOString()
                    }];

                    // Update title based on first user message
                    let newTitle = tab.title;
                    if (tab.title === 'New Chat' && message.role === 'user') {
                        newTitle = message.content.slice(0, 30) + (message.content.length > 30 ? '...' : '');
                    }

                    return {
                        ...tab,
                        messages: newMessages,
                        title: newTitle,
                        lastActivityAt: new Date().toISOString() // Update for session expiry
                    };
                }
                return tab;
            });

            return { ...prev, tabs: updatedTabs };
        });
    }, []);

    // Update last message (for streaming or editing)
    const updateLastMessage = useCallback((updates) => {
        setState(prev => {
            const updatedTabs = prev.tabs.map(tab => {
                if (tab.id === prev.activeTabId && tab.messages.length > 0) {
                    const newMessages = [...tab.messages];
                    newMessages[newMessages.length - 1] = {
                        ...newMessages[newMessages.length - 1],
                        ...updates
                    };
                    return { ...tab, messages: newMessages };
                }
                return tab;
            });

            return { ...prev, tabs: updatedTabs };
        });
    }, []);

    // Get active tab
    const getActiveTab = useCallback(() => {
        return state.tabs.find(t => t.id === state.activeTabId) || state.tabs[0];
    }, [state.tabs, state.activeTabId]);

    // Clear all history
    const clearAllHistory = useCallback(() => {
        const newState = createDefaultState();
        setState({ ...newState, isOpen: state.isOpen });
    }, [state.isOpen]);

    const value = {
        isOpen: state.isOpen,
        tabs: state.tabs,
        activeTabId: state.activeTabId,
        toggleChat,
        openChat,
        closeChat,
        setActiveTab,
        createTab,
        closeTab,
        addMessage,
        updateLastMessage,
        getActiveTab,
        clearAllHistory
    };

    return (
        <ChatContext.Provider value={value}>
            {children}
        </ChatContext.Provider>
    );
}

export function useChat() {
    const context = useContext(ChatContext);
    if (!context) {
        throw new Error('useChat must be used within a ChatProvider');
    }
    return context;
}

export default ChatContext;
